var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Checkbox, Chip, Autocomplete as RawAutocomplete, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { forwardRef, useMemo, useRef, useState } from 'react';
export var Autocomplete = function (_a) {
  var _b = _a.options,
    options = _b === void 0 ? [] : _b,
    _c = _a.getOptionValue,
    getOptionValue = _c === void 0 ? function (o) {
      var _a;
      return o == null ? o : (_a = o.id) !== null && _a !== void 0 ? _a : o;
    } : _c,
    multiple = _a.multiple,
    error = _a.error,
    inputRef = _a.inputRef,
    helperText = _a.helperText,
    placeholder = _a.placeholder,
    renderInput = _a.renderInput,
    props = __rest(_a, ["options", "getOptionValue", "multiple", "error", "inputRef", "helperText", "placeholder", "renderInput"]);
  var getOptionValueRef = useRef(getOptionValue);
  var selectedValue = useMemo(function () {
    var _a;
    var getSelectOption = function (v) {
      var _a;
      return (_a = options.find(function (o) {
        return getOptionValueRef.current(o) === v;
      })) !== null && _a !== void 0 ? _a : null;
    };
    if (!multiple) return getSelectOption(props.value);
    return ((_a = props.value) !== null && _a !== void 0 ? _a : []).map(getSelectOption).filter(function (o) {
      return o != null;
    });
  }, [multiple, options, props.value]);
  var handleChange = function (event, newValue) {
    var _a;
    var resolved = newValue;
    if (newValue != null) {
      if (multiple) {
        var safe = Array.isArray(newValue) ? newValue.filter(function (o) {
          return o != null;
        }) : [];
        resolved = safe.map(function (o) {
          return getOptionValue(o);
        });
      } else {
        resolved = getOptionValue(newValue);
      }
    }
    (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, resolved);
  };
  return _jsx(RawAutocomplete, __assign({
    fullWidth: true,
    disableClearable: true,
    getOptionLabel: function (o) {
      var _a;
      return (_a = o.name) !== null && _a !== void 0 ? _a : o.toString();
    },
    "data-testid": "autocomplete"
  }, props, {
    multiple: multiple,
    value: selectedValue,
    onChange: handleChange,
    options: options,
    renderInput: function (params) {
      return renderInput ? renderInput(params) : _jsx(TextField, __assign({
        error: error,
        helperText: helperText,
        placeholder: placeholder
      }, params));
    },
    noOptionsText: t('form.noOption', {
      ns: 'translation'
    }),
    ref: inputRef,
    closeText: t('autocomplete.close', {
      ns: 'translation'
    }),
    openText: t('autocomplete.open', {
      ns: 'translation'
    }),
    clearText: t('autocomplete.clear', {
      ns: 'translation'
    })
  }));
};
export var MultiAutocomplete = forwardRef(function (_a, ref) {
  var _b = _a.options,
    options = _b === void 0 ? [] : _b,
    _c = _a.getOptionValue,
    getOptionValue = _c === void 0 ? function (o) {
      return o.id;
    } : _c,
    _d = _a.getOptionLabel,
    getOptionLabel = _d === void 0 ? function (o) {
      var _a;
      return (_a = o === null || o === void 0 ? void 0 : o.name) !== null && _a !== void 0 ? _a : '';
    } : _d,
    _e = _a.value,
    value = _e === void 0 ? [] : _e,
    onChange = _a.onChange,
    placeholder = _a.placeholder,
    error = _a.error,
    helperText = _a.helperText,
    props = __rest(_a, ["options", "getOptionValue", "getOptionLabel", "value", "onChange", "placeholder", "error", "helperText"]);
  var SELECT_ALL_ID = -1;
  var filteredOptions = options.filter(function (item) {
    return item != null && item.id !== SELECT_ALL_ID;
  });
  var extendedOptions = useMemo(function () {
    return __spreadArray([{
      id: SELECT_ALL_ID,
      name: t('autocomplete.selectAll', {
        ns: 'translation'
      })
    }], filteredOptions, true);
  }, [SELECT_ALL_ID, filteredOptions]);
  var optionValues = useMemo(function () {
    return filteredOptions.map(getOptionValue);
  }, [filteredOptions, getOptionValue]);
  var selectedCount = useMemo(function () {
    return optionValues.filter(function (v) {
      return value.includes(v);
    }).length;
  }, [optionValues, value]);
  var isAllSelected = optionValues.length > 0 && selectedCount === optionValues.length;
  var isIndeterminate = selectedCount > 0 && selectedCount < optionValues.length;
  var _f = useState(false),
    open = _f[0],
    setOpen = _f[1];
  var handleChange = function (values) {
    var hasAll = values.includes(SELECT_ALL_ID);
    if (hasAll) {
      if (isAllSelected) {
        onChange === null || onChange === void 0 ? void 0 : onChange([]);
      } else {
        onChange === null || onChange === void 0 ? void 0 : onChange(optionValues);
        setOpen(false);
      }
      return;
    }
    var next = values.filter(function (v) {
      return v !== SELECT_ALL_ID;
    });
    onChange === null || onChange === void 0 ? void 0 : onChange(next);
    if (next.length === optionValues.length && optionValues.length > 0) {
      setOpen(false);
    }
  };
  return _jsx(Autocomplete, __assign({}, props, {
    multiple: true,
    disableCloseOnSelect: true,
    open: open,
    onOpen: function () {
      return setOpen(true);
    },
    onClose: function () {
      return setOpen(false);
    },
    options: extendedOptions,
    value: value,
    getOptionValue: getOptionValue,
    getOptionLabel: getOptionLabel,
    onChange: handleChange,
    inputRef: ref,
    placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : t('autocomplete.pleaseSelect', {
      ns: 'translation'
    }),
    error: error,
    helperText: helperText,
    renderOption: function (_props, option, _a) {
      var selected = _a.selected;
      var id = getOptionValue(option);
      if (id === SELECT_ALL_ID) {
        return _jsxs("li", __assign({}, _props, {
          style: {
            padding: '2px 8px'
          }
        }, {
          children: [_jsx(Checkbox, {
            size: "small",
            checked: isAllSelected,
            indeterminate: isIndeterminate,
            sx: {
              p: '8px',
              mr: '6px'
            }
          }), _jsx(Typography, {
            children: getOptionLabel(option)
          })]
        }));
      }
      return _jsxs("li", __assign({}, _props, {
        style: {
          padding: '2px 8px'
        }
      }, {
        children: [_jsx(Checkbox, {
          size: "small",
          checked: selected,
          sx: {
            p: '8px',
            mr: '6px'
          }
        }), _jsx(Typography, {
          children: getOptionLabel(option)
        })]
      }));
    },
    renderTags: function (tagValue) {
      var count = tagValue.length;
      if (count === 0) return null;
      if (isAllSelected) {
        return _jsx(Chip, {
          size: "small",
          label: t('autocomplete.allSelected', {
            ns: 'translation'
          })
        });
      }
      return _jsx(Chip, {
        size: "small",
        label: t('autocomplete.selectedCount', {
          count: count,
          ns: 'translation'
        })
      });
    }
  }));
});